<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import type { PreviewIncidentReportResponse } from '@roadfootage/api-client'
import { formatIncidentType, formatIncidentDateTime } from '../utils/format'

const props = defineProps<{
  isCluster: boolean
  incident: PreviewIncidentReportResponse | null
  clusterIncidents: PreviewIncidentReportResponse[]
  isLoading: boolean
  totalCount: number | null
  nearbyAddress: string | null
}>()

const emit = defineEmits<{
  close: []
  view: []
}>()

const router = useRouter()

const hasMore = computed(
  () => props.totalCount !== null && props.totalCount > props.clusterIncidents.length
)

const headerLabel = computed(() => {
  if (props.isLoading) return 'Loading...'
  if (props.isCluster) {
    const count = hasMore.value
      ? `${props.clusterIncidents.length}+`
      : `${props.totalCount ?? props.clusterIncidents.length}`
    const location = props.nearbyAddress ? ` near ${props.nearbyAddress}` : ''
    return `${count} incidents${location}`
  }
  return formatIncidentType(props.incident?.incidentReportType)
})

const navigateTo = (id: string | undefined) => {
  if (!id) return
  emit('view')
  router.push({ name: 'preview', params: { incidentReportId: id } })
}
</script>

<template>
  <q-card class="incident-map-popup shadow-4">
    <!-- Header -->
    <q-card-section class="row no-wrap items-center q-py-xs q-px-sm">
      <span class="text-caption text-bold text-grey-8 ellipsis" style="flex: 1; min-width: 0">{{
        headerLabel
      }}</span>
      <q-btn
        flat
        round
        dense
        size="sm"
        icon="mdi-close"
        class="q-ml-xs flex-shrink-0"
        @click="emit('close')"
      />
    </q-card-section>

    <q-separator />

    <!-- Loading skeleton -->
    <template v-if="isLoading">
      <q-skeleton height="120px" square />
      <q-card-section class="q-py-sm q-gutter-y-xs">
        <q-skeleton type="text" width="60%" />
        <q-skeleton type="text" width="80%" />
        <q-skeleton type="text" width="50%" />
      </q-card-section>
    </template>

    <!-- Single incident -->
    <template v-else-if="!isCluster && incident">
      <q-img
        v-if="incident.thumbnailUrl && !incident.hasContentWarning"
        :src="incident.thumbnailUrl"
        :ratio="16 / 9"
        class="cursor-pointer"
        @click="navigateTo(incident.id)"
      >
        <template #error>
          <div class="fit flex flex-center bg-grey-3">
            <q-icon name="mdi-image-broken" size="md" color="grey-6" />
          </div>
        </template>
      </q-img>
      <div v-else class="fallback-thumb flex flex-center bg-grey-3">
        <q-icon name="mdi-camera-off" size="md" color="grey-5" />
      </div>

      <q-card-section class="q-py-sm">
        <div class="text-overline text-primary q-mb-xs">
          {{ formatIncidentType(incident.incidentReportType) }}
        </div>
        <div class="row items-center no-wrap text-caption text-grey-7 q-mb-xs">
          <q-icon name="mdi-calendar" size="xs" class="q-mr-xs" />
          <span>{{
            formatIncidentDateTime(incident.incidentDateTime, incident.incidentTimezone)
          }}</span>
        </div>
        <div
          v-if="incident.address?.formattedAddress"
          class="row items-start no-wrap text-caption text-grey-7"
        >
          <q-icon name="mdi-map-marker" size="xs" class="q-mr-xs q-mt-xs" />
          <span class="address-clamp">{{ incident.address.formattedAddress }}</span>
        </div>
      </q-card-section>

      <q-card-actions align="right" class="q-pt-none q-pb-sm q-px-sm">
        <q-btn
          flat
          color="primary"
          label="View Incident"
          icon-right="mdi-arrow-right"
          size="sm"
          @click="navigateTo(incident.id)"
        />
      </q-card-actions>
    </template>

    <!-- Cluster list -->
    <template v-else-if="isCluster">
      <q-list separator class="cluster-list">
        <q-item
          v-for="item in clusterIncidents"
          :key="item.id"
          clickable
          v-ripple
          @click="navigateTo(item.id)"
          class="q-pa-none cluster-item"
        >
          <div class="cluster-thumb">
            <q-img
              v-if="item.thumbnailUrl && !item.hasContentWarning"
              :src="item.thumbnailUrl"
              fit="cover"
              style="height: 100%; width: 100%"
            >
              <template #error>
                <div class="fit flex flex-center bg-grey-3">
                  <q-icon name="mdi-image-broken" size="sm" color="grey-5" />
                </div>
              </template>
            </q-img>
            <div v-else class="fit flex flex-center bg-grey-3">
              <q-icon name="mdi-camera-off" size="sm" color="grey-5" />
            </div>
          </div>

          <div class="cluster-text q-px-sm q-py-sm">
            <div class="text-caption text-weight-medium text-grey-9 ellipsis">
              {{ formatIncidentType(item.incidentReportType) }}
            </div>
            <div class="text-caption text-grey-7 ellipsis">
              {{ formatIncidentDateTime(item.incidentDateTime, item.incidentTimezone) }}
            </div>
            <div
              v-if="item.address?.formattedAddress"
              class="text-caption text-grey-7 address-clamp"
            >
              {{ item.address.formattedAddress }}
            </div>
          </div>

          <q-icon
            name="mdi-chevron-right"
            color="grey-5"
            size="xs"
            class="q-mr-xs self-center flex-shrink-0"
          />
        </q-item>

        <!-- Overflow hint -->
        <q-item v-if="hasMore" class="cluster-overflow-hint q-py-sm">
          <q-item-section class="text-caption text-grey-6 text-center">
            <div>
              <q-icon name="mdi-magnify-plus-outline" size="xs" class="q-mr-xs" />
              Additional incidents not shown — zoom in to refine your search
            </div>
          </q-item-section>
        </q-item>
      </q-list>
    </template>
  </q-card>
</template>

<style lang="scss" scoped>
.incident-map-popup {
  width: 100%;
  overflow: hidden;
}

.fallback-thumb {
  height: 120px;
  width: 100%;
}

.cluster-thumb {
  width: 120px;
  min-width: 120px;
  align-self: stretch;
  flex-shrink: 0;
  overflow: hidden;
}

.cluster-text {
  flex: 1 1 0%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
}

.cluster-overflow-hint {
  background: $grey-2;
}

.cluster-list {
  max-height: 360px;
  overflow-y: auto;
}

.address-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
